/**
 * ProfileInfoSection (Detail View)
 * Displays profile name, username, bio, credentials, tags, and social links
 * Single column layout with inline stats and collapsible bio
 */

import {
  ChevronUp,
  Copy,
  Instagram,
  Link2,
  MapPin,
  MessageCircle,
  Pencil,
  Play,
  Settings,
  Upload,
  User,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
  EyeIcon,
  FacebookIcon,
  HamburgerIcon,
  HeartFilledIcon,
  HeartOutlineIcon,
  XIcon,
} from "@/components/icons"
import { ShareModal } from "@/components/shared/ShareModal"
import { TagChip } from "@/components/shared/TagChip"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { useProfileVideo } from "@/features/profile/hooks/useProfileVideo"
import { useTranslation } from "@/language"
import { cn, extractSocialHandle } from "@/lib"
import type { ProfileDetail } from "../types/profile.types"
import { FollowButton } from "./FollowButton"

interface ProfileInfoSectionProps {
  profile: ProfileDetail
  className?: string
  isOtherProfile?: boolean
  userId?: string
  introVideoUrl?: string
  intentions?: string[]
  onFollowersClick?: () => void
  onFollowingClick?: () => void
}

export function ProfileInfoSectionDetail({
  profile,
  className,
  isOtherProfile = false,
  userId,
  introVideoUrl,
  intentions = [],
  onFollowersClick,
  onFollowingClick,
}: ProfileInfoSectionProps) {
  const { t } = useTranslation("profile")
  const navigate = useNavigate()
  const createDmConversation = useCreateDmConversation()
  const { unfollow, follow } = useFollowActions()
  const [bioExpanded, _setBioExpanded] = useState(false)
  const [detailsExpanded, setDetailsExpanded] = useState(true)
  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const [isImageOpen, setIsImageOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  // Determine if bio should be collapsible (more than 3 lines worth of text)
  const isBioLong = profile.bio.length > 200

  // Fetch video for other profiles if introVideoUrl not provided
  const { data: fetchedVideo } = useProfileVideo(userId || "")
  const videoUrl = introVideoUrl || fetchedVideo?.filePath

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Image preview modal */}
      <Dialog open={isImageOpen} onOpenChange={setIsImageOpen}>
        <DialogContent className="h-[80vh] max-w-[1440px] sm:max-w-[1380px] p-12 bg-transparent shadow-none border-none flex [&_[data-slot='dialog-close']]:bg-[#F2F0EB0D] [&_[data-slot='dialog-close']]:text-[#F2F0EB] [&_[data-slot='dialog-close']]:border-[#F2F0EB26]">
          {profile.profilePhoto && (
            <img
              src={profile.profilePhoto}
              alt={profile.name}
              className="w-full max-h-full object-cover rounded-[40px]"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Video player modal */}
      <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
        <DialogContent className="h-[80vh] max-w-[1440px] sm:max-w-[1380px] p-12 bg-transparent shadow-none border-none [&_[data-slot='dialog-close']]:bg-[#F2F0EB0D] [&_[data-slot='dialog-close']]:text-[#F2F0EB] [&_[data-slot='dialog-close']]:border-[#F2F0EB26]">
          <video
            src={videoUrl}
            className="w-full h-full object-contain rounded-[40px]"
            controls
            autoPlay
          />
        </DialogContent>
      </Dialog>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={`/profile/${profile.id}`}
        shareText="Check out this profile on Rituality!"
        username={profile.username}
      />

      <div className="flex flex-col md:flex-row item-start justify-center md:gap-5">
        {/* Avatar on left, profile details on right (name, username, bio, stats) */}
        {/* Wrap in relative container for video icon overlay */}
        <div className="flex item-start justify-center">
          <div className="relative md:h-35 ">
            {profile.profilePhoto ? (
              <button type="button" onClick={() => setIsImageOpen(true)} className="cursor-pointer">
                <Avatar
                  src={profile.profilePhoto}
                  fallback={profile.name}
                  size="lg"
                  className="md:w-35 md:h-35 h-[140px] w-[140px] mb-5 rounded-full"
                />
              </button>
            ) : (
              <div className="md:w-35 md:h-35 h-[340px] w-[340px] md:rounded-full rounded-2xl bg-card flex items-center justify-center mb-5">
                <User className="w-9 h-9 text-muted-foreground" />
              </div>
            )}
            {/* Video icon overlay at bottom-right of avatar */}
            {videoUrl && (
              <button
                type="button"
                onClick={() => setIsVideoOpen(true)}
                className="absolute -bottom-1 md:-bottom-4 left-1/2 -translate-x-1/2 w-10 h-10 bg-base rounded-full flex items-center justify-center cursor-pointer transition-colors z-10"
                aria-label="Play introduction video"
              >
                <div className="bg-white w-5 h-4 rounded-xs flex items-center justify-center">
                  <Play className="w-3 h-3" fill="black" />
                </div>
              </button>
            )}
          </div>
        </div>

        <div className="w-full">
          {/* Name Row: Name (left) + Action Buttons (right) */}
          <div className="flex md:items-start items-center justify-center md:justify-between gap-4">
            <div className="flex flex-col items-center min-w-0">
              <h1 className="text-primary text-xl font-semibold leading-10 uppercase">
                {profile.name}
              </h1>
              <p className="text-[#F2F0EB66] text-sm">@{profile.username}</p>
            </div>

            {/* Action Buttons: Chat, Follow, Share, Hamburger */}
            <div className="hidden md:flex items-center gap-2.5 shrink-0">
              {isOtherProfile && (
                <>
                  {userId && profile.isFollowing && (
                    <div className="flex items-center gap-2 h-10 px-4">
                      <HeartFilledIcon className="size-5" color="#FFF" />
                      <span className="text-[14px] leading-[20px] font-semibold font-monteserat uppercase">
                        Followed
                      </span>
                    </div>
                  )}
                  {userId && !profile.isFollowing && (
                    <FollowButton
                      className="cursor-pointer uppercase px-8 md:px-12 h-[50px]"
                      targetUserId={userId}
                      variant="primary"
                      size="sm"
                      initialStatus={
                        profile.isFollowing !== undefined || profile.isPending !== undefined
                          ? {
                              isFollowing: profile.isFollowing ?? false,
                              isPending: profile.isPending ?? false,
                            }
                          : undefined
                      }
                    />
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-8 md:px-15 h-[50px] uppercase border border-white dark:border-black"
                    isLoading={createDmConversation.isPending}
                    onClick={async () => {
                      if (!userId) return
                      try {
                        const conversation = await createDmConversation.mutateAsync(userId)
                        navigate(`/chats/dm/${conversation.id}`)
                      } catch {
                        toast.error(t("profileInfo.failedToStartConversation"))
                      }
                    }}
                  >
                    {t("profileInfo.chat")}
                  </Button>
                </>
              )}

              {!isOtherProfile && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="items-center justify-center px-8 md:px-18 h-[45px] border border-gradient-3d rounded-md gap-3.5"
                    onClick={() => navigate("/my-rituality")}
                  >
                    <EyeIcon className="!w-5 !h-5" />
                    Back to Profile
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="hidden md:flex items-center justify-center px-8 md:px-12 h-[45px] gap-2.5 border  border-gradient-3d rounded-md"
                    onClick={() => navigate("/profile/edit")}
                  >
                    <Pencil className="!w-6 !h-5" />
                    Edit Profile
                  </Button>
                  <Button
                    variant="default"
                    size="icon"
                    onClick={() => navigate("/settings")}
                    className="hidden md:flex  h-11 w-11 border border-input border-gradient-3d rounded-md"
                    aria-label={t("profileInfo.settings")}
                  >
                    <Settings className="!w-5 !h-5" />
                  </Button>
                </>
              )}

              <Button
                variant="default"
                size="icon"
                className="h-11 w-11 border border-gradient-3d rounded-md"
                aria-label={t("profileInfo.share")}
                onClick={() => setShareOpen(true)}
              >
                <Upload className="!w-5 !h-5" />
              </Button>

              {/* {!isOtherProfile && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild className="md:hidden">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 border border-input rounded-md"
                      aria-label={t("profileInfo.menu")}
                    >
                      <HamburgerIcon className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="mt-1 p-2">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => navigate("/my-rituality")}
                    >
                      <EyeIcon className="w-6 h-6" />
                      Back to Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/profile/edit")}
                      className="cursor-pointer"
                    >
                      <Pencil />
                      Edit Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings")}
                      className="cursor-pointer"
                    >
                      <Settings />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer" onClick={() => setShareOpen(true)}>
                      <Share />
                      Share Profile
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )} */}

              {isOtherProfile && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 border border-input rounded-md"
                      aria-label={t("profileInfo.menu")}
                    >
                      <HamburgerIcon className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="mt-1 p-2">
                    {profile.isFollowing && (
                      <DropdownMenuItem
                        onClick={() => userId && unfollow(userId)}
                        className="cursor-pointer text-brand-terracotta"
                      >
                        <HeartFilledIcon className="w-4 h-4 mr-2" color="#a85544" />
                        {t("followButton.unfollow")}
                      </DropdownMenuItem>
                    )}
                    {!profile.isFollowing && (
                      <DropdownMenuItem
                        onClick={() => userId && follow(userId)}
                        className="cursor-pointer text-white md:hidden"
                      >
                        <HeartOutlineIcon className="w-4 h-4 mr-2" />
                        {t("followButton.follow")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={async () => {
                        try {
                          const profileLink = `${window.location.origin}/profile/${profile.id}`
                          await navigator.clipboard.writeText(profileLink)
                          toast.success(t("profileInfo.linkCopied"))
                        } catch {
                          toast.error(t("profileInfo.copyLinkFailed"))
                        }
                      }}
                      className="cursor-pointer"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      {t("profileInfo.copyLink")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        if (!userId) return
                        try {
                          const conversation = await createDmConversation.mutateAsync(userId)
                          navigate(`/chats/dm/${conversation.id}`)
                        } catch {
                          toast.error(t("profileInfo.failedToStartConversation"))
                        }
                      }}
                      className="cursor-pointer md:hidden"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {t("profileInfo.chat")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={e => {
                        e.stopPropagation()
                        setShareOpen(true)
                      }}
                      className="cursor-pointer md:hidden"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {t("profileInfo.share")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          <div className="flex md:hidden items-center justify-center gap-5 mt-2.5">
            <button
              type="button"
              className="text-sm text-charcoal cursor-pointer hover:opacity-80 transition-opacity"
              onClick={onFollowersClick}
            >
              <span className="font-semibold">{profile.followersCount}</span>{" "}
              <span className="font-semibold">{t("profileInfo.followers")}</span>
            </button>
            <button
              type="button"
              className="text-sm text-charcoal cursor-pointer hover:opacity-80 transition-opacity"
              onClick={onFollowingClick}
            >
              <span className="font-semibold">{profile.followingCount}</span>{" "}
              <span className="font-semibold">{t("profileInfo.following")}</span>
            </button>
          </div>
          {(profile.city || profile.state) && (
            <div className="flex md:hidden items-center justify-center gap-1.5 md:mb-3 text-sm text-primary mt-2.5">
              <MapPin className="w-5 h-5 shrink-0" />
              <span>
                {profile.city}
                {profile.city && profile.state && ", "}
                {profile.state}
              </span>
            </div>
          )}

          {isOtherProfile && (
            <div className="flex md:hidden w-full items-stretch justify-center gap-2.5 mt-5">
              {userId && (
                <FollowButton
                  className="cursor-pointer flex-1 uppercase py-3.75 h-[50px] text-xs sm:text-sm"
                  targetUserId={userId}
                  variant="primary"
                  size="sm"
                  initialStatus={
                    profile.isFollowing !== undefined || profile.isPending !== undefined
                      ? {
                          isFollowing: profile.isFollowing ?? false,
                          isPending: profile.isPending ?? false,
                        }
                      : undefined
                  }
                />
              )}
              <Button
                variant="outline"
                size="sm"
                className="flex-1 py-3.75 h-[50px] uppercase border border-white dark:border-black text-xs sm:text-sm"
                isLoading={createDmConversation.isPending}
                onClick={async () => {
                  if (!userId) return
                  try {
                    const conversation = await createDmConversation.mutateAsync(userId)
                    navigate(`/chats/dm/${conversation.id}`)
                  } catch {
                    toast.error(t("profileInfo.failedToStartConversation"))
                  }
                }}
              >
                {t("profileInfo.chat")}
              </Button>
            </div>
          )}
          {!isOtherProfile && (
            <div className="flex md:hidden w-full items-stretch justify-center gap-2.5 mt-5">
              <Button
                variant="outline"
                size="sm"
                className="items-center justify-center flex-1 py-3.75 h-[45px] border border-gradient-3d rounded-full gap-3.5"
                onClick={() => navigate("/my-rituality")}
              >
                <EyeIcon className="!w-5 !h-5" />
                Back to Profile
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center justify-center flex-1 py-3.75  h-[45px] gap-2.5 border  border-gradient-3d rounded-full"
                onClick={() => navigate("/profile/edit")}
              >
                <Pencil className="!w-6 !h-5" />
                Edit Profile
              </Button>
              <Button
                variant="default"
                size="icon"
                onClick={() => navigate("/settings")}
                className="hidden md:flex  h-11 w-11 border border-input border-gradient-3d rounded-md"
                aria-label={t("profileInfo.settings")}
              >
                <Settings className="!w-5 !h-5" />
              </Button>
            </div>
          )}

          {/* Followers/Following inline */}
          {detailsExpanded && (
            <div className="hidden md:flex items-center gap-4 mt-6">
              <button
                type="button"
                className="text-sm text-charcoal cursor-pointer hover:opacity-80 transition-opacity"
                onClick={onFollowersClick}
              >
                <span className="font-semibold">{profile.followersCount}</span>{" "}
                <span className="font-semibold">{t("profileInfo.followers")}</span>
              </button>
              <button
                type="button"
                className="text-sm text-charcoal cursor-pointer hover:opacity-80 transition-opacity"
                onClick={onFollowingClick}
              >
                <span className="font-semibold">{profile.followingCount}</span>{" "}
                <span className="font-semibold">{t("profileInfo.following")}</span>
              </button>
            </div>
          )}

          {/* Location with pin icon + city + state */}
          {detailsExpanded && (profile.city || profile.state) && (
            <div className="hidden md:flex items-center gap-1.5 mb-3 text-sm text-primary mt-7">
              <MapPin className="w-5 h-5 shrink-0" />
              <span>
                {profile.city}
                {profile.city && profile.state && ", "}
                {profile.state}
              </span>
            </div>
          )}

          {/* Bio with collapsible "Show less" */}
          {detailsExpanded && profile.bio && (
            <div className="">
              <p
                className={cn(
                  "text-primary text-sm leading-relaxed mt-7.5 md:mt-7",
                  !bioExpanded && isBioLong && "line-clamp-3",
                )}
              >
                {profile.bio}
              </p>
            </div>
          )}

          {/* Social links inline */}
          {detailsExpanded && (
            <div className="flex flex-wrap items-center gap-4 mt-6.25 md:mt-7">
              {profile.socials.website && (
                <a
                  href={
                    profile.socials.website.startsWith("http")
                      ? profile.socials.website
                      : `https://${profile.socials.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-primary hover:text-charcoal transition-colors font-semibold truncate"
                >
                  <Link2 className="w-5 h-5 font-semibold" />
                  <span>
                    {profile.socials.website.startsWith("http")
                      ? profile.socials.website
                      : `https://${profile.socials.website}`}
                  </span>
                </a>
              )}
              {profile.socials.twitter && (
                <a
                  href={
                    profile.socials.twitter.startsWith("http")
                      ? profile.socials.twitter
                      : `https://twitter.com/${profile.socials.twitter}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-primary hover:text-charcoal transition-colors font-semibold"
                >
                  <XIcon className="w-5 h-5 font-semibold" />
                  <span>{extractSocialHandle(profile.socials.twitter)}</span>
                </a>
              )}
              {profile.socials.instagram && (
                <a
                  href={
                    profile.socials.instagram.startsWith("http")
                      ? profile.socials.instagram
                      : `https://instagram.com/${profile.socials.instagram}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-primary hover:text-charcoal transition-colors font-semibold"
                >
                  <Instagram className="w-6 h-6 font-semibold" />
                  <span>{extractSocialHandle(profile.socials.instagram)}</span>
                </a>
              )}
              {profile.socials.facebook && (
                <a
                  href={
                    profile.socials.facebook.startsWith("http")
                      ? profile.socials.facebook
                      : `https://facebook.com/${profile.socials.facebook}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-primary hover:text-charcoal transition-colors font-semibold"
                >
                  <FacebookIcon className="w-6 h-6 font-semibold" />
                  <span>{extractSocialHandle(profile.socials.facebook)}</span>
                </a>
              )}
            </div>
          )}

          {/* Tags & Intentions */}
          {detailsExpanded && (profile.tags.length > 0 || intentions.length > 0) && (
            <div className="flex flex-wrap gap-3 mt-6.25 md:mt-6">
              {profile.tags.map((tag, index) => (
                <TagChip
                  key={`tag-${index}`}
                  tag={tag}
                  tab="members"
                  className="px-4 py-2 rounded-md border-white text-charcoal text-[13px]"
                />
              ))}
              {intentions.map((intention, index) => (
                <TagChip
                  key={`intention-${index}`}
                  tag={intention}
                  tab="members"
                  className="px-4 py-2 rounded-md border-white text-charcoal text-[13px]"
                />
              ))}
            </div>
          )}

          {/* Show Less/Show More toggle */}
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-primary hover:text-charcoal transition-colors mt-6.25 md:mt-7 mb-1 font-semibold"
            onClick={() => setDetailsExpanded(!detailsExpanded)}
          >
            {detailsExpanded ? (
              <>
                <span>{t("profileInfo.showLess")}</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span>{t("profileInfo.showMore")}</span>
                <ChevronUp className="w-4 h-4 rotate-180" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
